// The Prompt logs screen's view model (API-016, REQ-244): when the Activity tab's Prompt logs
// sub-tab is present and why it is absent, the list's rows and filters, and the decoding of one
// stored body. Pure and DOM-free (tests/unit/prompt-logs/model.test.ts); fetching and rendering
// live in prompt-logs.tsx.
//
// Prompts are restricted data. Nothing here keeps a body: `decodePromptBody` hands the document
// to its caller and holds no reference, and the screen keeps it only in the open dialog.
import type { components } from "../../api/schema";
import type { ChipTone } from "../../primitives/page";
import type { FieldErrors } from "../../primitives/forms";
import type { ScopeMe } from "../../scope/scope";
import { personName } from "../quotas/names";

export type PromptLogSummary = components["schemas"]["PromptLogSummary"];
export type BodyStatus = components["schemas"]["BodyStatus"];
export type User = components["schemas"]["User"];

// ---------------------------------------------------------------------------------------------
// Who sees the sub-tab (REQ-244)

/**
 * Whether the org logs prompts and whether this principal may read them. The proxy has it from
 * `/api/me` (`promptLogging`, the acting admin's `canReadPrompts`); the AllCode admin builds it
 * from the org's settings and the `prompts:read` the API listed for the session
 * (`holdsPromptReader`), which a super-admin holds by rank once the API grants it. `canRead` is
 * the one prompt-reader answer: nothing here second-guesses it by role.
 */
export interface PromptLogging {
  enabled: boolean;
  canRead: boolean;
}

/**
 * The org's prompt logging as a shell knows it: the pair once known; "loading" while the shell
 * reads it; "failed" when that read failed; null when nothing has said yet (the proxy before the
 * first settings pull of its run).
 */
export type PromptLoggingState = PromptLogging | "loading" | "failed" | null;

/** What the sub-tab's predicate reads: the principal, and the org's prompt logging as known. */
export interface PromptLogsScope {
  me: ScopeMe | null;
  promptLogging: PromptLoggingState;
}

export const LOGGING_OFF_REASON = "Prompt logging is off for this organisation";
export const NOT_A_READER_REASON = "You can't read prompts";
export const LOGGING_UNKNOWN_REASON = "Prompt logging isn't known yet for this organisation";
export const LOGGING_CHECK_FAILED_REASON = "Couldn't check prompt logging.";

/** Why the sub-tab is absent: its setting loading, its read failed, not said yet, logging off, or not a reader. */
export type PromptLogsAbsence = "loading" | "failed" | "unknown" | "off" | "not-reader";

const ABSENT_REASONS: Readonly<Record<Exclude<PromptLogsAbsence, "loading">, string>> = {
  failed: LOGGING_CHECK_FAILED_REASON,
  unknown: LOGGING_UNKNOWN_REASON,
  off: LOGGING_OFF_REASON,
  "not-reader": NOT_A_READER_REASON,
};

/**
 * Why the sub-tab is absent, or null when it is present. Not known (loading, failed, not said yet)
 * is absent, so the sub-tab fails closed, but it never reads as "off": only `enabled === false`
 * does. Logging off comes before not a reader, since with logging off there is nothing to read.
 */
export function promptLogsAbsence({ promptLogging }: PromptLogsScope): PromptLogsAbsence | null {
  if (promptLogging === "loading" || promptLogging === "failed") return promptLogging;
  if (promptLogging === null) return "unknown";
  if (!promptLogging.enabled) return "off";
  if (!promptLogging.canRead) return "not-reader";
  return null;
}

/** The words for an absent sub-tab; null when it is present, or while its setting loads (no words yet). */
export function promptLogsAbsentReason(scope: PromptLogsScope): string | null {
  const absence = promptLogsAbsence(scope);
  return absence === null || absence === "loading" ? null : ABSENT_REASONS[absence];
}

/** The sub-tab is present only when the org logs prompts and the principal may read them. */
export function promptLogsVisible(scope: PromptLogsScope): boolean {
  return promptLogsAbsence(scope) === null;
}

// ---------------------------------------------------------------------------------------------
// One event, opened from a link

/** The query keys the Prompt logs screen opens one event's summary from (`?event=…&ts=…`). */
export const PROMPT_LOG_EVENT_PARAM = "event";
export const PROMPT_LOG_TS_PARAM = "ts";

/** One event, as the Prompt logs screen's single-event read locates it. */
export interface PromptLogTarget {
  eventId: string;
  ts: string;
}

/** The search (`event=…&ts=…`, no `?`) a link to one event's summary appends to the sub-tab's URL. */
export function promptLogSearch(target: PromptLogTarget): string {
  return new URLSearchParams({ [PROMPT_LOG_EVENT_PARAM]: target.eventId, [PROMPT_LOG_TS_PARAM]: target.ts }).toString();
}

/** The event a Prompt logs URL asks to open, or null unless it names both the event and its time. */
export function promptLogTarget(query: URLSearchParams): PromptLogTarget | null {
  const eventId = query.get(PROMPT_LOG_EVENT_PARAM)?.trim() ?? "";
  const ts = query.get(PROMPT_LOG_TS_PARAM)?.trim() ?? "";
  return eventId !== "" && ts !== "" ? { eventId, ts } : null;
}

export const AUDITED_NOTE = "Each view is recorded in the audit log.";

// ---------------------------------------------------------------------------------------------
// The list

export interface PromptLogRow {
  eventId: string;
  ts: string;
  userId: string;
  /** The person as the API names them: display name, else email, else external id, else "Unknown user". */
  user: string;
  model: string;
  messageCount: number;
  bytesRaw: number;
  size: string;
  bodyStatus: BodyStatus;
  status: { label: string; tone: ChipTone };
  /** True when a body can be fetched (`stored`, or stored but truncated). */
  readable: boolean;
}

const STATUS_CHIPS: Readonly<Record<BodyStatus, { label: string; tone: ChipTone }>> = {
  stored: { label: "Stored", tone: "green" },
  truncated: { label: "Stored, truncated", tone: "amber" },
  pending: { label: "Pending", tone: "grey" },
  none: { label: "No body", tone: "grey" },
  missing: { label: "Missing", tone: "grey" },
  spool_failed: { label: "Spool failed", tone: "red" },
  too_large: { label: "Too large", tone: "amber" },
  discarded_by_policy: { label: "Discarded by policy", tone: "grey" },
  erased: { label: "Erased", tone: "grey" },
};

export function statusChip(status: BodyStatus): { label: string; tone: ChipTone } {
  return STATUS_CHIPS[status];
}

/** A body exists and may be fetched: the API's `IsReadable` statuses. */
export function bodyReadable(status: BodyStatus): boolean {
  return status === "stored" || status === "truncated";
}

/** 0 → "0 B", 1536 → "1.5 kB", 2_621_440 → "2.5 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  const units = ["kB", "MB", "GB"];
  let value = bytes;
  let unit = -1;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  const text = value >= 100 ? value.toFixed(0) : value.toFixed(1).replace(/\.0$/, "");
  return `${text} ${units[unit]}`;
}

// ---------------------------------------------------------------------------------------------
// The user filter: a picker over the one page of users the screen loads (`listUsers` has no
// search parameter), resolved to the user's id here, so the query still carries an id. The rows
// never need this page: the API names each row's person itself.

/** How many users the screen loads for the picker. */
export const USER_PAGE_LIMIT = 500;

/** Shown under the picker when the org has more users than the screen loaded. */
export const USERS_TRUNCATED_NOTE = `Showing the first ${USER_PAGE_LIMIT} users.`;

/** Whether the users page left some out: a next cursor, or a full page. */
export function usersTruncated(page: { items: readonly unknown[]; nextCursor?: string | null }): boolean {
  return (page.nextCursor ?? null) !== null || page.items.length >= USER_PAGE_LIMIT;
}

/** "Dana Lee (dana@example.com)"; a missing part is left out; `personName` when both are (an AWS
 *  principal's name, else "Unknown user"), never the external id as it is (IA-1). */
export function userLabel(user: User): string {
  const name = user.displayName?.trim() ?? "";
  const email = user.email?.trim() ?? "";
  if (name !== "" && email !== "") return `${name} (${email})`;
  return name || email || personName(user);
}

/** The users whose name, email or external id contains the query, ignoring case, by label. */
export function matchUsers(users: readonly User[], query: string, limit = 50): User[] {
  const q = query.trim().toLowerCase();
  const byLabel = (a: User, b: User) => userLabel(a).localeCompare(userLabel(b)) || a.id.localeCompare(b.id);
  const hit = (user: User) => [user.displayName, user.email, user.externalId].some((part) => (part ?? "").toLowerCase().includes(q));
  return users
    .filter((user) => q === "" || hit(user))
    .sort(byLabel)
    .slice(0, limit);
}

export function promptLogRow(item: PromptLogSummary): PromptLogRow {
  return {
    eventId: item.eventId,
    ts: item.ts,
    userId: item.userId,
    user: personName({ displayName: item.displayName, email: item.email, externalId: item.userExternalId }),
    model: item.model ?? "—",
    messageCount: item.messageCount,
    bytesRaw: item.bytesRaw,
    size: formatBytes(item.bytesRaw),
    bodyStatus: item.bodyStatus,
    status: statusChip(item.bodyStatus),
    readable: bodyReadable(item.bodyStatus),
  };
}

// ---------------------------------------------------------------------------------------------
// Filters → the list query

export interface PromptLogFilters {
  /** The picked user's id (the picker resolves a name or email to it), or blank for every user. */
  userId: string;
  model: string;
  /** `YYYY-MM-DD` (UTC) or blank. */
  from: string;
  to: string;
}

export const EMPTY_PROMPT_LOG_FILTERS: PromptLogFilters = { userId: "", model: "", from: "", to: "" };

export const PROMPT_LOG_PAGE_SIZE = 100;

const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function validatePromptLogFilters(filters: PromptLogFilters): FieldErrors {
  const errors: FieldErrors = {};
  if (filters.from !== "" && !DAY.test(filters.from)) errors.from = "A date, YYYY-MM-DD.";
  if (filters.to !== "" && !DAY.test(filters.to)) errors.to = "A date, YYYY-MM-DD.";
  if (!errors.from && !errors.to && filters.from !== "" && filters.to !== "" && filters.from > filters.to) errors.to = "Not before the start.";
  if (filters.model.length > 300) errors.model = "At most 300 characters.";
  return errors;
}

export interface PromptLogQuery {
  from?: string;
  to?: string;
  userId?: string;
  model?: string;
  limit: number;
  cursor?: string;
}

/** The day after a `YYYY-MM-DD` day (UTC), so an inclusive "to" picks the API's exclusive bound. */
export function dayAfter(day: string): string {
  const next = new Date(Date.parse(`${day}T00:00:00Z`) + 24 * 60 * 60 * 1000);
  return next.toISOString().slice(0, 10);
}

/** The `listPromptLogs` query: blanks left out, `to` made exclusive, one page at a time. */
export function promptLogQuery(filters: PromptLogFilters, cursor: string | null = null): PromptLogQuery {
  const query: PromptLogQuery = { limit: PROMPT_LOG_PAGE_SIZE };
  if (filters.from !== "") query.from = filters.from;
  if (filters.to !== "") query.to = dayAfter(filters.to);
  if (filters.userId.trim() !== "") query.userId = filters.userId.trim();
  if (filters.model.trim() !== "") query.model = filters.model.trim();
  if (cursor !== null) query.cursor = cursor;
  return query;
}

// ---------------------------------------------------------------------------------------------
// One body: the PromptBody v1 document (contracts/prompt-body.v1.schema.json), decoded here

export interface TextBlock {
  type: "text";
  text: string;
}
export interface ToolUseBlock {
  type: "tool_use";
  id: string;
  name: string;
  input: Record<string, unknown>;
}
export interface ToolResultBlock {
  type: "tool_result";
  toolUseId: string;
  content: string | TextBlock[];
  isError?: boolean;
}
export type ContentBlock = TextBlock | ToolUseBlock | ToolResultBlock;

export interface PromptMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string | ContentBlock[];
  name?: string;
}

export interface PromptBody {
  schemaVersion: 1;
  eventId: string;
  request: { system?: string | TextBlock[] | null; messages: PromptMessage[]; tools?: { name: string }[] };
  response: { content: ContentBlock[]; stopReason?: string | null };
  truncated: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The document, or null when the JSON is not a PromptBody v1 (the reader shows "unreadable"). */
export function parsePromptBody(json: unknown): PromptBody | null {
  if (!isRecord(json) || json["schemaVersion"] !== 1 || typeof json["eventId"] !== "string") return null;
  const request = json["request"];
  const response = json["response"];
  if (!isRecord(request) || !Array.isArray(request["messages"])) return null;
  if (!isRecord(response) || !Array.isArray(response["content"])) return null;
  if (typeof json["truncated"] !== "boolean") return null;
  return json as unknown as PromptBody;
}

/** Whether this browser can inflate gzip itself; otherwise the dialog offers the download. */
export function canInflate(): boolean {
  return typeof DecompressionStream === "function";
}

/** Inflate the gzip object the API streams and parse it; rejects when it is not a PromptBody v1. */
export async function decodePromptBody(compressed: ArrayBuffer | Uint8Array): Promise<PromptBody> {
  const bytes = compressed instanceof Uint8Array ? compressed : new Uint8Array(compressed);
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream("gzip"));
  const body = parsePromptBody(JSON.parse(await new Response(stream).text()) as unknown);
  if (body === null) throw new Error("not a PromptBody v1 document");
  return body;
}

// ---------------------------------------------------------------------------------------------
// Rendering a body: turns of labelled blocks; an image is named, never shown

export type RenderedBlock =
  | { kind: "text"; text: string }
  | { kind: "tool_use"; label: string; json: string }
  | { kind: "tool_result"; label: string; text: string; isError: boolean }
  | { kind: "opaque"; label: string };

export interface RenderedTurn {
  key: string;
  /** "System", "User", "Assistant", "Tool", "Response". */
  label: string;
  blocks: RenderedBlock[];
}

const ROLE_LABELS: Readonly<Record<PromptMessage["role"], string>> = { system: "System", user: "User", assistant: "Assistant", tool: "Tool" };

function textOf(content: string | TextBlock[]): string {
  return typeof content === "string" ? content : content.map((block) => block.text).join("\n");
}

/** A base64 payload's size in bytes, for the "[image, N bytes]" label. */
function base64Bytes(data: string): number {
  const padding = data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((data.length * 3) / 4) - padding);
}

/** An image block (from a dialect the neutral shape does not carry) is named by its size only. */
function opaqueLabel(block: Record<string, unknown>): string {
  const type = typeof block["type"] === "string" ? block["type"] : "unknown";
  if (type === "image") {
    const source = isRecord(block["source"]) ? block["source"] : {};
    const data = typeof source["data"] === "string" ? source["data"] : typeof block["data"] === "string" ? block["data"] : "";
    return `[image, ${base64Bytes(data)} bytes]`;
  }
  return `[${type} block]`;
}

export function renderBlock(block: unknown): RenderedBlock {
  if (!isRecord(block)) return { kind: "opaque", label: "[unknown block]" };
  switch (block["type"]) {
    case "text":
      return { kind: "text", text: typeof block["text"] === "string" ? block["text"] : "" };
    case "tool_use":
      return { kind: "tool_use", label: `Tool call: ${String(block["name"])}`, json: JSON.stringify(block["input"] ?? {}, null, 2) };
    case "tool_result":
      return {
        kind: "tool_result",
        label: `Tool result for ${String(block["toolUseId"])}`,
        text: textOf((block["content"] ?? "") as string | TextBlock[]),
        isError: block["isError"] === true,
      };
    default:
      return { kind: "opaque", label: opaqueLabel(block) };
  }
}

function renderContent(content: unknown): RenderedBlock[] {
  if (typeof content === "string") return [{ kind: "text", text: content }];
  return Array.isArray(content) ? content.map(renderBlock) : [];
}

/** The system prompt, each request turn in order, then the response as its own turn. */
export function renderTurns(body: PromptBody): RenderedTurn[] {
  const turns: RenderedTurn[] = [];
  if (body.request.system) turns.push({ key: "system", label: "System", blocks: renderContent(body.request.system) });
  body.request.messages.forEach((message, index) => {
    const label = ROLE_LABELS[message.role] ?? String(message.role);
    turns.push({ key: `m${index}`, label: message.name ? `${label} (${message.name})` : label, blocks: renderContent(message.content) });
  });
  turns.push({ key: "response", label: "Response", blocks: renderContent(body.response.content) });
  return turns;
}

/** "Stopped: end_turn · 3 tools offered · truncated" — the body's facts line. */
export function bodyFacts(body: PromptBody): string[] {
  const facts: string[] = [];
  if (body.response.stopReason) facts.push(`Stopped: ${body.response.stopReason}`);
  const tools = body.request.tools?.length ?? 0;
  if (tools > 0) facts.push(`${tools} ${tools === 1 ? "tool" : "tools"} offered`);
  if (body.truncated) facts.push("Truncated to fit the size limit; not a faithful copy");
  return facts;
}
